import { projectionTarget, sameProjectionOwner, validProjectionApproval } from '../contracts/projectionObserver.contract.js';
import { createProjectionReceiptQueue } from './projectionReceiptQueue.service.js';

/** Maps an accepted transport target to the existing reducer's immutable view object. */
export function createProjectionObserver({ enabled, clock, getOwner, getSequence, getRows, send, build = null, longTasks = null }) {
  let approval = null, approvedOwner = null, representations = new WeakMap();
  const current = entry => enabled === true && !!approval && getOwner()?.socket?.connected === true
    && sameProjectionOwner(entry.owner, getOwner()) && sameProjectionOwner(approvedOwner, getOwner());
  const queue = createProjectionReceiptQueue({ clock, current, send, longTasks });
  const pendingRepresentations = new Set();
  const diagnostics = { targets: 0, reducerRejected: 0, sequenceMismatch: 0, rowMissing: 0,
    rowUnchanged: 0, queued: 0, paintSuperseded: 0, paintTextMismatch: 0, paintNotVisible: 0,
    supersededBeforeCommit: 0, supersededAfterCommit: 0, supersededRemoval: 0, supersededReplacement: 0 };
  const count = key => { diagnostics[key] = Math.min(65535, diagnostics[key] + 1); };
  const miss = (entry, key) => {
    if (!entry || entry.miss || !queue.has(entry.receipt) || !current(entry.receipt)) return false;
    entry.miss = key; count(key); return true;
  };
  const rowCurrent = entry => {
    const rows = getRows();
    const currentRow = Array.isArray(rows) ? rows.find(row => String(row.fixtureId ?? row.id) === entry.target.fixtureId) : null;
    if (currentRow !== entry.row) {
      entry.retired = true;
      if (!entry.receipt.painted && miss(entry, 'paintSuperseded')) {
        count(entry.committed ? 'supersededAfterCommit' : 'supersededBeforeCommit');
        count(currentRow ? 'supersededReplacement' : 'supersededRemoval');
      }
    }
    return !entry.retired;
  };
  // Called synchronously by the sole row writer, including remove/readd within
  // one React batch. No transport pump or sequence mutation at this boundary.
  const rowsChanged = () => {
    // At most maxPending entries. A later unsampled reducer can supersede a
    // sampled object before React commits it, so a paint hook may never run.
    for (const entry of pendingRepresentations) {
      if (!queue.has(entry.receipt)) pendingRepresentations.delete(entry);
      else rowCurrent(entry);
    }
  };
  const sync = () => {
    if (!sameProjectionOwner(approvedOwner, getOwner()) || !getOwner()?.socket?.connected) {
      approval = null; approvedOwner = null; representations = new WeakMap(); pendingRepresentations.clear(); queue.cancel();
      longTasks?.stop();
    }
    queue.sync();
    rowsChanged();  };
  const represented = (entry, row) => {
    const owned = entry && queue.has(entry.receipt) && current(entry.receipt) && entry.row === row;
    // Admission still binds the exact room sequence. At paint, a different
    // fixture's later sequence cannot replace this immutable component row.
    return owned && rowCurrent(entry);
  };
  return Object.freeze({
    sync, rowsChanged,
    attestation() {
      const socket = getOwner()?.socket;
      return enabled === true && socket?.connected && typeof socket.id === 'string'
        && /^[A-Za-z0-9_-]{1,128}$/.test(socket.id) && typeof build === 'string'
        && /^[a-f0-9]{40}$/.test(build) ? { session: socket.id, build } : null;
    },
    approve(owner, reply, startedAt) {
      sync();
      if (enabled !== true || !sameProjectionOwner(owner, getOwner())
          || !validProjectionApproval(reply, owner) || !Number.isFinite(startedAt)
          || startedAt > clock.now() || clock.now() - startedAt > 2000) { longTasks?.stop(); return false; }
      const next = reply.observer;
      if (approval && ['session', 'room', 'generation', 'build'].some(key => approval[key] !== next[key])) queue.cancel();
      approval = Object.freeze({ session: next.session, room: next.room, generation: next.generation,
        build: next.build, publicRoom: next.publicRoom, serverNow: reply.serverNow, startedAt });
      approvedOwner = owner;
      longTasks?.start();
      return true;
    },
    applied(envelope, rows, previousRows, accepted) {
      sync();
      if (enabled !== true || !approval) return;
      const target = projectionTarget(envelope, approval);
      if (!target) return;
      count('targets');
      if (accepted !== true) { count('reducerRejected'); return; }
      if (getSequence() !== target.sequence) { count('sequenceMismatch'); return; }
      // Redis time at registration plus the whole request RTT is an upper clock
      // bound. Never restart the server deadline at packet reception or retry.
      const expires = approval.startedAt + target.deadline - approval.serverNow;
      const row = rows.find(value => String(value.fixtureId ?? value.id) === target.fixtureId);
      const previous = previousRows.find(value => String(value.fixtureId ?? value.id) === target.fixtureId);
      if (!row) { count('rowMissing'); return; }
      if (row === previous) { count('rowUnchanged'); return; }
      const entry = queue.add(getOwner(), target, approval.publicRoom, expires);
      if (!entry) return;
      count('queued');
      const representation = { receipt: entry, target, row, miss: null, retired: false, committed: false };
      representations.set(row, representation); pendingRepresentations.add(representation);
    },
    token(row, text) {
      const entry = representations.get(row);
      const valid = represented(entry, row);
      if (valid && text !== entry.target.text) miss(entry, 'paintTextMismatch');
      // The score hook requests a token in layout effect after this exact row commits.
      if (valid && text === entry.target.text) entry.committed = true;
      return valid && text === entry.target.text ? entry : null;
    },
    current: (entry, row, text) => represented(entry, row) && text === entry.target.text,
    painted(entry, row, text) { if (represented(entry, row) && text === entry.target.text) queue.paint(entry.receipt); },
    cancel() { approval = null; approvedOwner = null; representations = new WeakMap(); pendingRepresentations.clear(); queue.cancel(); longTasks?.stop(); },
    inspect: queue.inspect,
    notVisible: entry => miss(entry, 'paintNotVisible'),
    diagnostics: () => ({ ...diagnostics, ...queue.diagnostics(), ...longTasks?.diagnostics() }),
  });
}
